// UK postcode helpers — frontend.
//
// Used on the address and employer postcode inputs to give live feedback
// before submission. We mirror the same validator on the backend so an
// invalid optional postcode (e.g. employer postcode for an unemployed
// applicant) is dropped silently rather than failing the broker payload.
//
// Regex source: Royal Mail / GDS recommended pattern. Covers the full set
// of valid UK formats (e.g. "M1 1AA", "SW1A 0AA", "GIR 0AA"). Case-insensitive.
// Allows the space between outward and inward codes to be optional.

// Permissive UK postcode regex (GDS / Royal Mail recommended). Matches the
// structural shape — 1-2 outward letters, 1-2 digits + optional letter, space,
// 1 digit, 2 letters — without trying to enforce every letter-position rule.
// Catches malformed input like "Lon" or "SW1" while accepting all real
// postcodes including edge cases (CR2 6XH, EC1A 1BB, GIR 0AA).
const UK_POSTCODE_REGEX = /^(GIR ?0AA|[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2})$/i;

export const isValidUKPostcode = (postcode) => {
  if (!postcode || typeof postcode !== 'string') return false;
  return UK_POSTCODE_REGEX.test(postcode.trim().toUpperCase());
};

// Format to canonical "SW1A 1AA" with a single space before the inward code.
// Strips other whitespace. Returns the input unchanged if it doesn't match
// the structural pattern (so users mid-typing don't get yanked around).
export const formatUKPostcode = (postcode) => {
  if (!postcode || typeof postcode !== 'string') return postcode;
  const cleaned = postcode.replace(/\s+/g, '').toUpperCase();
  if (cleaned.length < 5) return postcode;
  // Inward code is always the last 3 chars (e.g. "1AA"), outward is the rest.
  return `${cleaned.slice(0, -3)} ${cleaned.slice(-3)}`;
};

export default isValidUKPostcode;
